import { useCallback, useMemo, useState } from 'react';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import useFetchCrmObjectProperties from 'framework-asset-preview-lib/internal/shared/hooks/useFetchCrmObjectProperties';
import useAssetPreviewTracking from 'framework-asset-preview-lib/internal/shared/hooks/useAssetPreviewTracking';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { getPropertyValue } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { fetchTrackingUrlsAsCrmSearchResponse } from 'framework-asset-preview-lib/internal/objectTypes/TRACKING_URL/api';
import { TRACKING_URL_PAGE_SIZE } from 'framework-asset-preview-lib/utils/constants';
import AssociatedAssets from './AssociatedAssets';
import AssetPaginator from './AssetPaginator';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function AssociationGroupWithFetchedObjects({
  associationGroup,
  crmObject,
  defaultOpen,
  hoverAssetId,
  onHoverAsset
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [currentData, setCurrentData] = useState(associationGroup);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();
  const {
    properties,
    error: errorProperties,
    loading: loadingProperties
  } = useFetchCrmObjectProperties(associationGroup.toObjectTypeId, {
    skip: !open || associationGroup.toObjectTypeId === OBJECT_TYPE.TRACKING_URL
  });
  const isTrackingUrl = associationGroup.toObjectTypeId === OBJECT_TYPE.TRACKING_URL;
  const supportsPagination = isTrackingUrl && associationGroup.hasMore !== undefined;
  const fetchPage = useCallback(async offset => {
    if (!isTrackingUrl) {
      return;
    }
    setIsLoadingPage(true);
    try {
      const campaignGuid = getPropertyValue(crmObject, 'hs_origin_asset_id');
      const response = await fetchTrackingUrlsAsCrmSearchResponse(campaignGuid, offset);
      setCurrentData(Object.assign({}, currentData, {
        associatedObjects: response.results,
        hasMore: response.hasMore,
        total: response.total,
        offset: response.offset
      }));
    } catch (error) {
      console.error('Error fetching tracking URLs page:', error);
    } finally {
      setIsLoadingPage(false);
    }
  }, [crmObject, currentData, isTrackingUrl]);
  const handleOpenChange = useCallback(event => {
    const isOpen = Boolean(event.target.value);
    setOpen(event.target.value);
    trackAssetPreviewInteraction({
      action: isOpen ? 'expand association group' : 'collapse association group'
    });
  }, [trackAssetPreviewInteraction]);
  const handleNextClick = useCallback(() => {
    if (!currentData.hasMore || currentData.offset === undefined) {
      return;
    }
    const nextOffset = currentData.offset + TRACKING_URL_PAGE_SIZE;
    void fetchPage(nextOffset);
  }, [currentData.hasMore, currentData.offset, fetchPage]);
  const handlePrevClick = useCallback(() => {
    if (currentData.offset === undefined || currentData.offset === 0) {
      return;
    }
    const prevOffset = Math.max(0, currentData.offset - TRACKING_URL_PAGE_SIZE);
    void fetchPage(prevOffset);
  }, [currentData.offset, fetchPage]);
  const isLoading = useMemo(() => loadingProperties || isLoadingPage, [loadingProperties, isLoadingPage]);
  const isError = useMemo(() => !isLoading && Boolean(errorProperties), [errorProperties, isLoading]);
  const isEmptyState = useMemo(() => Boolean(!isLoading && !isError && currentData.associatedObjects && currentData.associatedObjects.length === 0), [currentData.associatedObjects, isError, isLoading]);
  return /*#__PURE__*/_jsxs(ExpandableSection, {
    indentation: "compact",
    titleText: associationGroup.objectTypeName,
    open: open,
    onOpenChange: handleOpenChange,
    children: [/*#__PURE__*/_jsx(AssociatedAssets, {
      associatedObjects: currentData.associatedObjects || [],
      associationGroup: currentData,
      crmObject: crmObject,
      hoverAssetId: hoverAssetId,
      isEmptyState: isEmptyState,
      isError: isError,
      isLoading: isLoading,
      onHoverAsset: onHoverAsset,
      properties: properties
    }), supportsPagination && currentData.associatedObjects && currentData.associatedObjects.length > 0 ? /*#__PURE__*/_jsx(AssetPaginator, {
      isNextDisabled: !currentData.hasMore || isLoadingPage,
      isPrevDisabled: currentData.offset === undefined || currentData.offset === 0 || isLoadingPage,
      onNextClick: handleNextClick,
      onPrevClick: handlePrevClick
    }) : null]
  });
}